import { useEffect, useRef, useState, useCallback } from "react";
import { Button } from "primereact/button";
import { InputText } from "primereact/inputtext";
import { Dropdown } from "primereact/dropdown";
import { RadioButton } from "primereact/radiobutton";
import { AutoComplete } from "primereact/autocomplete";
import { Toast } from "primereact/toast";
import { Dialog } from "primereact/dialog";
import { Skeleton } from "primereact/skeleton";
import CandidateResults, { Candidate } from "./CandidateResult";
import MonsterCandidateProfile from "./Monstercandidateprofile";
import { Fetch } from "../../../shared/Fetch";
import { SavedSearchState, SearchFilterProvider, useSearchFilters } from "./SearchFilterContext";
import { citizenshipAPIMap, clearanceIDMap, jobTypeAPIMap, minuteKeyMap, radiusOptions, travelMap } from "./searchOptions";
import { MonsterSavedSearchesPanel } from "./MonsterSavedSearchesPanel";

// ─── Local-only option lists (not shared with the sidebar form) ───────────────

const GeometricPattern = () => (
  <svg style={{ position: "absolute", inset: 0, width: "100%", height: "100%", opacity: 0.18, pointerEvents: "none" }} xmlns="http://www.w3.org/2000/svg">
    <defs>
      <pattern id="geo" x="0" y="0" width="80" height="80" patternUnits="userSpaceOnUse">
        <polygon points="0,0 40,0 0,40" fill="none" stroke="#a78bfa" strokeWidth="1" />
        <polygon points="80,80 40,80 80,40" fill="none" stroke="#a78bfa" strokeWidth="1" />
        <circle cx="60" cy="20" r="14" fill="none" stroke="#a78bfa" strokeWidth="1" />
        <circle cx="20" cy="60" r="14" fill="none" stroke="#a78bfa" strokeWidth="1" />
        <line x1="0" y1="80" x2="80" y2="0" stroke="#a78bfa" strokeWidth="0.5" />
        <polygon points="40,0 80,0 80,40" fill="#6d28d9" opacity="0.4" />
        <polygon points="0,40 0,80 40,80" fill="#6d28d9" opacity="0.4" />
      </pattern>
    </defs>
    <rect width="100%" height="100%" fill="url(#geo)" />
  </svg>
);

const ProfileSkeleton = () => (
  <div style={{ padding: "16px" }}>
    <div style={{ display: "flex", gap: "16px", marginBottom: "24px" }}>
      <Skeleton shape="circle" size="64px" />
      <div style={{ flex: 1 }}>
        <Skeleton width="200px" height="20px" style={{ marginBottom: "8px" }} />
        <Skeleton width="140px" height="16px" style={{ marginBottom: "8px" }} />
        <Skeleton width="180px" height="16px" />
      </div>
    </div>
    <Skeleton width="100%" height="120px" style={{ marginBottom: "16px" }} />
    <Skeleton width="100%" height="80px" style={{ marginBottom: "16px" }} />
    <Skeleton width="100%" height="80px" />
  </div>
);

interface SearchParams {
  jobTitle?: string[];
  skillEntries?: any[];
  location?: any[];
  radius?: string;
  searchType?: string;
  booleanEntries?: { name: string; importance: string };
  [key: string]: any;
}

interface SkillEntry {
  id: number;
  name: string;
  importance: "Required" | "NiceToHave";
}

// ─── Shared request builder ─────────────────────────────────────────────────

const buildLoadParams = (
  searchState: {
    jobTitleEntries: { id: number; name: string }[];
    skillEntries: SkillEntry[];
    location: string[];
    radius: string;
    searchType: string;
    booleanEntries: { name: string; importance: string };
  },
  filters: any = {},
  overrides: { pageNum?: number } = {},
): SearchParams => ({
  jobTitles: searchState.jobTitleEntries.filter((j) => j.name.trim()).map((j) => j.name.trim()),
  skillEntries: searchState.skillEntries,
  location: searchState.location,
  radius: searchState.radius,
  searchType: searchState.searchType,
  booleanEntries: searchState.booleanEntries,
  limitRelocate: filters.limitRelocate ?? false,
  limitVeterans: filters.limitVeterans ?? false,
  workAuthorizations: filters.workAuthorizations ?? [],
  careerLevel: filters.careerLevel,
  yearsOfExperience: filters.yearsOfExperience,
  yearsExp: filters.yearsExp,
  expRequired: filters.expRequired,
  resumeUpdatedMaxAge: filters.resumeUpdatedMaxAge,
  resumeUpdatedMinAge: filters.resumeUpdatedMinAge,
  lastActiveMaxAge: filters.lastActiveMaxAge,
  candidateName: filters.candidateName,
  jobType: filters.jobType,
  citizenship: filters.citizenship,
  clearance: filters.clearance ?? filters.securityClearances,
  salary: filters.salary,
  salaryAmount: filters.salaryAmount,
  includeWithoutSalary: filters.includeWithoutSalary,
  travel: filters.willingnessToTravel ?? filters.travel,
  education: filters.minEducationLevel ?? filters.education,
  minEducationLevel: filters.minEducationLevel,
  willingnessToTravel: filters.willingnessToTravel,
  languageProficiencyLevels: filters.languageProficiencyLevels,
  degrees: filters.degrees,
  companies: filters.companies,
  jobTenure: filters.jobTenure,
  pageNum: overrides.pageNum ?? 1,
  veteranCandidates: filters.veteranCandidates,
  willingnessToRelocate: filters.willingnessToRelocate,
  excludeKeywords: filters.excludeKeywords,
});

// ─── Outer shell: creates the toast ref, then mounts the provider ─────────────

const SearchMonster = (props: any) => {
  const toast = useRef<any>(null);
  return (
    <SearchFilterProvider toast={toast}>
      <SearchMonsterInner {...props} toast={toast} />
    </SearchFilterProvider>
  );
};

// ─── Inner component: everything that used to be in SearchMonster ────────────

const SearchMonsterInner = ({ onSwitchToClassic, onSwitchToPower, reload, toast }: any) => {
  // ── Shared with the sidebar filter form — pulled from context instead of
  const { searchType, setSearchType, booleanEntries, setBooleanEntries, skillEntries, addSkill, removeSkill, updateSkillName, toggleSkillImportance, jobTitleEntries, addJobTitle, removeJobTitle, updateJobTitle, location, setLocation, suggestions, searchLocation, restoreState, radius, setRadius, filters } = useSearchFilters();

  // ── Local-only state (not needed anywhere else, so it stays as useState) ──
  // const [showAdvanced, setShowAdvanced] = useState(false);
  // const [resumeUpdated, setResumeUpdated] = useState("all");
  // const [education, setEducation] = useState("all");
  // const [jobType, setJobType] = useState("all");
  // const [travel, setTravel] = useState("none");
  // const [relocation, setRelocation] = useState(false);
  const [showResults, setShowResults] = useState(false);
  const [apiResults, setApiResults] = useState<Candidate[]>([]);
  const [totalResults, setTotalResults] = useState(0);
  const [totalReturned, setTotalReturned] = useState(0);
  const [selectedCandidate, setSelectedCandidate] = useState<Candidate | null>(null);
  const [profileVisible, setProfileVisible] = useState(false);
  const [first, setFirst] = useState(1);
  const [isLoading, setIsLoading] = useState(false);
  const [profileLoading, setProfileLoading] = useState(false);
  const [useageData, setUseageData] = useState({ customerId: 0, quota: 0, viewsConsumed: 0, viewsRemaining: 0 });
  const [saveDialogVisible, setSaveDialogVisible] = useState(false);
  const [saveName, setSaveName] = useState("");
  const [saveBtnHover, setSaveBtnHover] = useState(false);

  const handleSaveSearch = async () => {
    const searchName = saveName.trim() || searchQueryString.trim();

    if (!searchName) {
      toast.current?.show({ severity: "error", detail: "Search Name is required.", life: 3000 });
      return;
    }
    const snapshot: SavedSearchState = { searchType, jobTitleEntries, skillEntries, location, radius, booleanEntries, filters };

    await Fetch("Monster/SaveSearch", "POST", { searchName: searchName, searchType, searchState: snapshot }, "", toast);

    toast.current?.show({ severity: "success", detail: "Search saved", life: 2000 });
    setSaveDialogVisible(false);
    setSaveName("");
  };

  const checkUsageBeforeSearch = useCallback(async () => {
    try {
      const response = await Fetch("Monster/getViewAccess", "GET", null, `profile_ID=forViewCount`, toast);
      if (response) {
        const data = await response.json();
        if (data.isAccess === true) {
          setUseageData({
            customerId: 0,
            quota: data.maxViewsAllowed || 0,
            viewsConsumed: data.viewsConsumed || 0,
            viewsRemaining: (data.maxViewsAllowed || 0) - (data.viewsConsumed || 0),
          });
        } else if (data.isAccess === false) {
          toast?.current.show({ severity: "error", detail: data.message || "Something Went Wrong or Your Do Not Have View Limit", life: 3000 });
        }
      }
    } catch (err) {
      console.error(err);
    }
  }, [toast]);

  useEffect(() => {
    checkUsageBeforeSearch();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  // ── Search state bundle passed into buildLoadParams ────────────────────────
  const searchState = { jobTitleEntries, skillEntries, location, radius, searchType, booleanEntries };

  const loadTableData = useCallback(
    async (params: SearchParams = {}) => {
      setIsLoading(true);

      const securityClearance = (() => {
        if (!params?.clearance) return [];
        if (Array.isArray(params.clearance)) return params.clearance;
        return Object.entries(params.clearance)
          .filter(([_, v]) => v === true)
          .map(([k]) => ({ clearanceId: clearanceIDMap[k], countryAbbrev: "US" }))
          .filter((x) => x.clearanceId);
      })();

      const jobTypes = (() => {
        if (!params?.jobType) return [];
        if (typeof params.jobType === "object" && !Array.isArray(params.jobType)) {
          return Object.entries(params.jobType)
            .filter(([_, v]) => v === true)
            .map(([k]) => jobTypeAPIMap[k])
            .filter(Boolean);
        }
        if (typeof params.jobType === "string") {
          if (params.jobType === "all" || !params.jobType) return [];
          const simpleMap: Record<string, string> = {
            fulltime: "WantsPermanent",
            parttime: "WantsTemp",
            contract: "WantsContract",
            intern: "WantsIntern",
            seasonal: "WantsSeasonal",
          };
          return [simpleMap[params.jobType]].filter(Boolean);
        }
        return [];
      })();

      // ── Companies ────────────────────────────────────────────────────────

      // ── Company ──────────────────────────────────────────────────────────
      const company = (() => {
        if (!Array.isArray(params?.companies) || !params.companies.length) return undefined;
        const mapped = params.companies.filter((c: string) => c?.trim()).map((c: string) => ({ name: c.trim() }));
        return mapped.length ? mapped : undefined;
      })();

      const legalStatuses = (() => {
        if (!params?.citizenship) return [];
        if (typeof params.citizenship === "string") {
          const mapped = citizenshipAPIMap[params.citizenship];
          return mapped ? [{ legalStatusName: mapped, countryAbbrev: "US" }] : [];
        }
        if (typeof params.citizenship === "object" && !Array.isArray(params.citizenship)) {
          return Object.entries(params.citizenship)
            .filter(([_, v]) => v === true)
            .map(([k]) => ({ legalStatusName: citizenshipAPIMap[k], countryAbbrev: "US" }))
            .filter((x) => x.legalStatusName);
        }
        return [];
      })();

      // ── Resume / activity ages ───────────────────────────────────────────

      const resolveMinutes = (val: any): number | undefined => {
        if (!val || val === "all") return undefined;
        if (minuteKeyMap[val]) return minuteKeyMap[val];
        const n = Number(val);
        return Number.isFinite(n) && n > 0 ? n : undefined;
      };

      const resumeUpdatedMaxAge = resolveMinutes(params?.resumeUpdatedMaxAge ?? params?.resumeUpdated);
      const resumeUpdatedMinAge = resolveMinutes(params?.resumeUpdatedMinAge);
      const lastActiveMaxAge = resolveMinutes(params?.lastActiveMaxAge);

      // ── Years of experience ──────────────────────────────────────────────
      const yearsOfExperience = (() => {
        let v = params?.yearsOfExperience ?? params?.yearsExp;
        if (!v) return undefined;
        if (typeof v === "object" && v.expression) {
          v = v.expression;
        }
        v = String(v).trim();
        if (!v) return undefined;

        // "2-3", "2 - 3"
        const rangeMatch = v.match(/^(\d+)\s*-\s*(\d+)$/);
        if (rangeMatch) {
          return { expression: `${rangeMatch[1]}-${rangeMatch[2]}`, importance: "Required" };
        }

        // ">5", "> 5"  →  "5+"
        const gtMatch = v.match(/^>\s*(\d+)$/);
        if (gtMatch) {
          return { expression: `${gtMatch[1]}+`, importance: "Required" };
        }

        // "5+", "8+"  → already in the format Monster wants
        const plusMatch = v.match(/^(\d+)\s*\+$/);
        if (plusMatch) {
          return { expression: `${plusMatch[1]}+`, importance: "Required" };
        }

        // "<8", "< 8"  →  "0-8"
        const ltMatch = v.match(/^<\s*(\d+)$/);
        if (ltMatch) {
          return { expression: `0-${ltMatch[1]}`, importance: "Required" };
        }

        // plain "4"
        if (/^\d+$/.test(v)) {
          return { expression: v, importance: "Required" };
        }

        return undefined;
      })();

      // ── Work Authorization ───────────────────────────────────────────────
      const workAuthorizations = params?.workAuthorizations?.length ? params.workAuthorizations : [];

      // ── Skills (semantic) ────────────────────────────────────────────────
      const skills = (() => {
        if (params?.searchType === "boolean") return undefined;
        if (!Array.isArray(params?.skillEntries)) return undefined;
        const filtered = params.skillEntries.filter((s: SkillEntry) => s.name?.trim());
        if (!filtered.length) return undefined;
        return filtered.map((s: SkillEntry) => ({
          name: s.name.trim(),
          importance: s.importance,
        }));
      })();

      // ── Boolean expression ───────────────────────────────────────────────
      // const booleanExpression = (() => {
      //   if (params?.searchType !== "boolean") return undefined;
      //   const expr = params?.booleanEntries?.name ?? "";
      //   if (!expr) return undefined;
      //   return { expression: String(expr), importance: "Required" };
      // })();
      const booleanExpression = (() => {
        if (params?.searchType !== "boolean") return undefined;
        const expr = params?.booleanEntries?.name ?? "";
        if (!expr) return undefined;

        const excludeTerms: string[] = Array.isArray(params?.excludeKeywords) ? params.excludeKeywords : [];
        const excludeClause = excludeTerms.length ? excludeTerms.map((t) => (t.includes(" ") ? `NOT "${t}"` : `NOT ${t}`)).join(" ") : "";

        const combined = [expr.trim(), excludeClause].filter(Boolean).join(" ");
        return { expression: combined, importance: "NiceToHave" };
      })();

      // ── Salary ────────────────────────────────────────────────────────────
      // const salary = {
      //   minimum: params?.salary?.minimum ? params?.salary?.minimum : null,
      //   currencyAbbrev: "USD",
      //   includeResumesWithoutSalary: params?.salary?.includeResumesWithoutSalary ?? params?.includeWithoutSalary ?? true,
      // };

      // ── Education ─────────────────────────────────────────────────────────
      const simpleEducationMap: Record<string, string> = {
        hs: "HighSchoolOrEquivalent",
        associate: "AssociateDegree",
        bachelor: "BachelorsDegree",
        master: "MastersDegree",
        phd: "Doctorate",
      };

      const minEducationLevel = (() => {
        if (params?.minEducationLevel) return params.minEducationLevel;
        if (params?.education && params.education !== "all") return simpleEducationMap[params.education];
        return undefined;
      })();

      const willingnessToTravel = (() => {
        if (params?.willingnessToTravel) return params.willingnessToTravel;
        if (params?.travel && params.travel !== "none") return travelMap[params.travel];
        return undefined;
      })();

      // ── Language Proficiency ─────────────────────────────────────────────
      const languageProficiencyLevels = params?.languageProficiencyLevels ?? null;

      // ── Build final request ──────────────────────────────────────────────
      const requestBody = {
        country: "US",
        searchType: "Semantic",
        page: params?.pageNum ?? 1,
        perPage: 20,
        semantic: {
          ...(Array.isArray(params?.jobTitles) &&
            params.jobTitles.length > 0 && {
              jobTitles: params.jobTitles,
            }),
          ...(skills && { skills }),
          ...(booleanExpression && { booleanExpression }),
          ...(params?.location
            ? {
                locations: params?.location?.map((loc: any) => ({
                  city: loc?.City || "",
                  state: loc?.State,
                  postalCode: loc?.Zip || "",
                  country: "US",
                  radius: parseInt(params.radius ?? "30"),
                  radiusUnit: "Miles",
                })),
              }
            : { locations: [] }),
          ...(resumeUpdatedMaxAge !== undefined && { resumeUpdatedMaximumAge: resumeUpdatedMaxAge }),
          ...(resumeUpdatedMinAge !== undefined && { resumeUpdatedMinimumAge: resumeUpdatedMinAge }),
          ...(lastActiveMaxAge !== undefined && { lastActiveMaximumAge: lastActiveMaxAge }),
          ...(yearsOfExperience && { yearsOfExperience }),
          ...(params?.candidateName && { candidateName: params.candidateName }),
          ...(company && { company }),
          // ...(typeof params?.veteranCandidates === "boolean" && { veteranCandidates: params.veteranCandidates }),
          // ...(typeof params?.willingnessToRelocate === "boolean" && { willingnessToRelocate: params.willingnessToRelocate }),
          ...(params?.veteranCandidates === true && { veteranCandidates: true }),
          ...(params?.willingnessToRelocate === true && { willingnessToRelocate: true }),
          ...(workAuthorizations.length > 0 && { workAuthorizations }),
          ...(legalStatuses.length > 0 && { legalStatuses }),
          ...(securityClearance.length > 0 && { securityClearances: securityClearance }),
          ...(jobTypes.length > 0 && { jobTypes }),
          ...(minEducationLevel && { minEducationLevel }),
          ...(willingnessToTravel && { willingnessToTravel }),
          ...(languageProficiencyLevels && { languageProficiencyLevels }),
          // salary,
        },
      };

      try {
        const response = await Fetch("Monster/searchResumes", "POST", requestBody, "", toast);

        if (response) {
          const dataA: any = await response.json();
          const data: any = dataA.candidates || [];
          const raw: any[] = data.resumes ?? data.candidates ?? (Array.isArray(data) ? data : []);
          const mapMonsterToCandidateResult = (item: any): Candidate => ({
            id: item.identity?.textResumeID ?? item.resumeID ?? item.id,
            textResumeID: item.identity?.textResumeID,
            matchScore: item.relevance?.score ?? 0,
            name: item.identity?.name ?? "Unknown",
            location: [item.location?.city, item.location?.state, item.location?.country].filter(Boolean).join("-"),
            currentTitle: item.relevance?.experience?.title?.name ?? "—",
            company: item.relevance?.experience?.company?.name ?? "—",
            resumeUpdated: item.identity?.resumeModifiedDate ? new Date(item.identity.resumeModifiedDate).toLocaleDateString("en-US") : "—",
            experience: item.yearsOfExperience ? `${item.yearsOfExperience} Years` : "—",
            highestEducation: item.degree ?? "—",
            targetJobTitle: "—",
            desiredJobType: "—",
            desiredSalary: "—",
            relocation: item.location?.willRelocate ? "Will Relocate" : "Won't Relocate",
            authorization: item.location?.workAuthorizations?.[0]?.country ?? "—",
            topSkills: (item.relevance?.skills ?? []).slice(0, 3).map((s: any) => ({
              name: s.name,
              years: `${s.yearsOfExperience?.toFixed(1) ?? "?"}yrs`,
            })),
          });

          setApiResults(raw.map(mapMonsterToCandidateResult));
          setShowResults(true);
          setTotalResults(dataA?.boards?.[0]?.matched ?? data.total ?? raw.length ?? 0);
          setTotalReturned(dataA?.boards?.[0]?.returned ?? 0);
        }
      } catch (err) {
        console.error("Search failed:", err);
        toast.current?.show({ severity: "error", summary: "Search Failed", detail: "Something went wrong.", life: 3000 });
      } finally {
        setIsLoading(false);
      }
    },
    [toast],
  );

  const getViewAccess = useCallback(
    async (candidate: Candidate) => {
      if (!candidate.textResumeID) {
        toast.current?.show({ severity: "warn", summary: "Missing ID", detail: "No resume ID available for this candidate.", life: 3000 });
        return;
      }

      try {
        const response = await Fetch("Monster/getViewAccess", "GET", null, `profile_ID=${candidate.textResumeID}`, toast);
        if (!response) return;

        const data = await response.json();

        if (data.isAccess) {
          setSelectedCandidate(null);
          setProfileLoading(true);
          setProfileVisible(true);
          setTimeout(() => {
            setSelectedCandidate(candidate);
          }, 0);
          setUseageData({
            customerId: 0,
            quota: data.maxViewsAllowed || 0,
            viewsConsumed: data.viewsConsumed || 0,
            viewsRemaining: (data.maxViewsAllowed || 0) - (data.viewsConsumed || 0),
          });
        } else {
          toast.current?.show({ severity: "error", summary: "Access Denied", detail: data.message || "You do not have access to view this profile.", life: 4000 });
        }
      } catch (err) {
        console.error("getViewAccess error:", err);
      }
    },
    [toast],
  );

  const handleRestoreSearch = useCallback(
    (state: SavedSearchState) => {
      restoreState(state);
      setFirst(1);
    },
    [restoreState],
  );
  // ── onPage / onUpdateFilters / search button now all reduce to one line via buildLoadParams ──

  const onPage = useCallback(
    async (page: number) => {
      await loadTableData(buildLoadParams(searchState, filters, { pageNum: page }));
    },
    // eslint-disable-next-line react-hooks/exhaustive-deps
    [loadTableData, jobTitleEntries, skillEntries, location, radius, searchType, booleanEntries, filters],
  );

  const handleUpdateFilters = useCallback(
    (newFilters: any) => {
      setFirst(1);
      loadTableData(buildLoadParams(searchState, newFilters, { pageNum: 1 }));
    },
    // eslint-disable-next-line react-hooks/exhaustive-deps
    [loadTableData, jobTitleEntries, skillEntries, location, radius, searchType, booleanEntries],
  );

  const handleSearch = useCallback(async () => {
    setShowResults(true);
    await checkUsageBeforeSearch();
    await loadTableData(buildLoadParams(searchState, {}, { pageNum: 1 }));
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [checkUsageBeforeSearch, loadTableData, jobTitleEntries, skillEntries, location, radius, searchType, booleanEntries]);

  const searchQueryString = [
    jobTitleEntries
      .filter((j) => j.name)
      .map((j) => j.name)
      .join(", "),
    skillEntries
      .filter((s) => s.name)
      .map((s) => s.name)
      .join(", "),
  ]
    .filter(Boolean)
    .join(", ");

  return (
    <div style={{ fontFamily: "'IBM Plex Sans', sans-serif", backgroundColor: "#f3f4f6" }}>
      {/* ── Hero Banner ── */}
      {showResults === false && (
        <div style={{ position: "relative", backgroundColor: "#1e0a3c", padding: "48px 32px 52px", overflow: "hidden" }}>
          <GeometricPattern />

          <h1 style={{ position: "relative", textAlign: "center", color: "#fff", fontSize: "26px", fontWeight: 700, marginBottom: "28px", letterSpacing: "0.01em" }}>Monster Search helps you connect with the right candidates - now.</h1>

          {/* ── Search Bar ── */}
          <div style={{ position: "relative", maxWidth: "1100px", margin: "0 auto", display: "flex", alignItems: "stretch", gap: "10px" }}>
            <div style={{ flex: 1, minWidth: 0, backgroundColor: "#fff", borderRadius: "6px", display: "flex", overflow: "hidden", boxShadow: "0 4px 24px rgba(0,0,0,0.35)" }}>
              {searchType === "semantic" && (
                <div style={{ flex: "1 1 0", borderRight: "1px solid #e5e7eb", padding: "10px 14px" }}>
                  <div style={{ fontSize: "10px", fontWeight: 700, color: "#6b21a8", textTransform: "uppercase", letterSpacing: "0.08em", marginBottom: "6px" }}>JOB TITLE</div>

                  <div style={{ display: "flex", flexDirection: "column", gap: "5px" }}>
                    {jobTitleEntries.map((jt, idx) => (
                      <div key={jt.id} style={{ display: "flex", alignItems: "center", gap: "6px" }}>
                        {jobTitleEntries.length > 1 ? (
                          <button onClick={() => removeJobTitle(jt.id)} style={{ background: "none", border: "none", cursor: "pointer", color: "#9ca3af", padding: "0 2px", fontSize: "14px", lineHeight: 1, flexShrink: 0 }} title="Remove job title">
                            ×
                          </button>
                        ) : (
                          <span style={{ width: "18px", flexShrink: 0 }} />
                        )}
                        <InputText value={jt.name} onChange={(e) => updateJobTitle(jt.id, e.target.value)} placeholder={idx === 0 ? "Cook, Developer, Nurse" : "Add title..."} className="border-none shadow-none p-0" style={{ flex: 1, fontSize: idx === 0 ? "16px" : "14px", color: "black", fontWeight: "bolder", minWidth: 0 }} />
                      </div>
                    ))}
                  </div>

                  {/* <button onClick={addJobTitle} style={{ marginTop: "6px", background: "none", border: "none", cursor: "pointer", color: "#7c3aed", fontSize: "11px", fontWeight: 600, padding: "2px 0", textDecoration: "underline" }}>
                    + Add another job title
                  </button> */}
                </div>
              )}

              {searchType === "semantic" ? (
                <div style={{ flex: "1.5 1 0", borderRight: "1px solid #e5e7eb", padding: "10px 14px", minWidth: 0 }}>
                  <div style={{ fontSize: "10px", fontWeight: 700, color: "#6b21a8", textTransform: "uppercase", letterSpacing: "0.08em", marginBottom: "6px" }}>SKILLS / KEYWORDS</div>

                  <div style={{ display: "flex", flexDirection: "column", gap: "5px" }}>
                    {skillEntries.map((skill, idx) => (
                      <div key={skill.id} style={{ display: "flex", alignItems: "center", gap: "6px" }}>
                        {skillEntries.length > 1 ? (
                          <button onClick={() => removeSkill(skill.id)} style={{ background: "none", border: "none", cursor: "pointer", color: "#9ca3af", padding: "0 2px", fontSize: "14px", lineHeight: 1, flexShrink: 0 }} title="Remove skill">
                            ×
                          </button>
                        ) : (
                          <span style={{ width: "18px", flexShrink: 0 }} />
                        )}

                        <InputText value={skill.name} onChange={(e) => updateSkillName(skill.id, e.target.value)} placeholder={idx === 0 ? "e.g. Java, React, SQL" : "Add skill..."} className="border-none shadow-none p-0" style={{ flex: 1, fontSize: "14px", color: "black", fontWeight: 600, minWidth: 0 }} />

                        <button
                          onClick={() => toggleSkillImportance(skill.id)}
                          style={{
                            flexShrink: 0,
                            display: "inline-flex",
                            alignItems: "center",
                            gap: "5px",
                            padding: "3px 8px",
                            borderRadius: "20px",
                            border: `1.5px solid ${skill.importance === "Required" ? "#7c3aed" : "#16a34a"}`,
                            backgroundColor: skill.importance === "Required" ? "#ede9fe" : "#f0fdf4",
                            color: skill.importance === "Required" ? "#6d28d9" : "#15803d",
                            fontSize: "10px",
                            fontWeight: 700,
                            cursor: "pointer",
                            whiteSpace: "nowrap",
                            letterSpacing: "0.04em",
                            transition: "all 0.15s",
                          }}
                          title="Click to toggle Required / Nice to have"
                        >
                          <span style={{ width: "13px", height: "13px", borderRadius: "50%", border: `2px solid ${skill.importance === "Required" ? "#7c3aed" : "#16a34a"}`, display: "inline-flex", alignItems: "center", justifyContent: "center", fontSize: "8px" }}>{skill.importance === "Required" ? "R" : "N"}</span>
                          {skill.importance === "Required" ? "Required" : "Nice to have"}
                        </button>
                      </div>
                    ))}
                  </div>

                  <button onClick={addSkill} style={{ marginTop: "6px", background: "none", border: "none", cursor: "pointer", color: "#7c3aed", fontSize: "11px", fontWeight: 600, padding: "2px 0", textDecoration: "underline" }}>
                    + Add another skill or keyword
                  </button>
                </div>
              ) : (
                <div style={{ flex: "3 1 0", borderRight: "1px solid #e5e7eb", padding: "10px 14px" }}>
                  <div style={{ fontSize: "10px", fontWeight: 700, color: "#6b21a8", textTransform: "uppercase", letterSpacing: "0.08em", marginBottom: "4px" }}>BOOLEAN</div>
                  <InputText value={booleanEntries?.name ?? ""} onChange={(e) => setBooleanEntries({ name: e.target.value, importance: "Required" })} placeholder="(e.g. Sales AND Marketing NOT Audit)" className="w-full border-none shadow-none p-0" style={{ fontSize: "16px", color: "black", fontWeight: "bolder" }} />
                </div>
              )}

              <div style={{ flex: "1.2 1 0", borderRight: "1px solid #e5e7eb", padding: "10px 14px" }}>
                <div style={{ fontSize: "10px", fontWeight: 700, color: "#6b21a8", textTransform: "uppercase", letterSpacing: "0.08em", marginBottom: "4px" }}>LOCATION</div>

                <AutoComplete
                  value={location}
                  suggestions={suggestions}
                  completeMethod={searchLocation}
                  field="label"
                  onChange={(e: any) => {
                    const newValue = e.value.map((item: any) => {
                      if (typeof item === "object" && item?.label) return item;

                      let label = "";
                      if (typeof item === "string") {
                        label = item;
                      } else if (item?.Name) {
                        label = item.Name;
                      } else if (item?.Zip && !item?.City) {
                        label = item.Zip;
                      } else {
                        label = `${item?.City ?? ""}, ${item?.State ?? ""}`;
                      }

                      return typeof item === "string" ? { label } : { ...item, label };
                    });
                    setLocation(newValue);
                  }}
                  multiple
                  itemTemplate={(item: any) => (
                    <div>
                      {item?.Name ? (
                        <strong>{item?.Name || ""}</strong>
                      ) : (
                        <span>
                          <strong>{item.City}</strong>, {item.State}
                        </span>
                      )}
                    </div>
                  )}
                  selectedItemTemplate={(item: any) => (typeof item === "string" ? item : item?.label ?? item?.Name ?? `${item?.City ?? ""}, ${item?.State ?? ""}`)}
                  placeholder="Search by City, State or ZIP"
                  className="border-none w-full"
                  style={{ width: "100%" }}
                />
              </div>

              <div style={{ flex: "0.8 1 0", borderRight: "1px solid #e5e7eb", padding: "10px 14px" }}>
                <div style={{ fontSize: "10px", fontWeight: 700, color: "#6b21a8", textTransform: "uppercase", letterSpacing: "0.08em", marginBottom: "4px" }}>RADIUS</div>
                <Dropdown value={radius} options={radiusOptions} onChange={(e) => setRadius(e.value)} className="w-full border-none shadow-none" style={{ fontSize: "16px", color: "black", fontWeight: "bolder" }} />
              </div>

              <Button label="SEARCH" onClick={handleSearch} style={{ backgroundColor: "#9333ea", border: "none", borderRadius: "0", fontSize: "15px", fontWeight: 700, padding: "0 28px", letterSpacing: "0.05em", flexShrink: 0 }} />
            </div>

            <Button
              icon="pi pi-bookmark"
              onClick={() => setSaveDialogVisible(true)}
              tooltip="Save this search"
              tooltipOptions={{ position: "top" }}
              onMouseEnter={() => setSaveBtnHover(true)}
              onMouseLeave={() => setSaveBtnHover(false)}
              style={{ backgroundColor: saveBtnHover ? "#f3e8ff" : "#fff", color: "#7c3aed", border: "none", borderRadius: "6px", width: "52px", flexShrink: 0, fontSize: "17px", boxShadow: "0 4px 24px rgba(0,0,0,0.35)", transition: "background-color 0.15s ease" }}
            />
          </div>

          {/* ── Search Type Radios ── */}
          <div style={{ position: "relative", maxWidth: "1100px", margin: "14px auto 0", display: "flex", alignItems: "center", justifyContent: "space-between", flexWrap: "wrap", gap: "10px" }}>
            <div className="flex align-items-center gap-4">
              <div className="flex align-items-center gap-2">
                <RadioButton
                  inputId="semantic"
                  value="semantic"
                  checked={searchType === "semantic"}
                  onChange={() => {
                    setSearchType("semantic");
                    setBooleanEntries({ name: "", importance: "Required" });
                  }}
                />
                <label htmlFor="semantic" style={{ color: "#e9d5ff", fontSize: "13px", cursor: "pointer" }}>
                  Semantic Search
                </label>
              </div>
              <div className="flex align-items-center gap-2">
                <RadioButton
                  inputId="boolean"
                  value="boolean"
                  checked={searchType === "boolean"}
                  onChange={() => {
                    setSearchType("boolean");
                  }}
                />
                <label htmlFor="boolean" style={{ color: "#e9d5ff", fontSize: "13px", cursor: "pointer" }}>
                  Semantic Boolean Keyword Search
                </label>
              </div>
            </div>
          </div>

          {/* ── Advanced Options Panel (legacy inline version) ── */}
          {/* {showAdvanced && (
            <div style={{ position: "relative", maxWidth: "1100px", margin: "14px auto 0", backgroundColor: "rgba(255,255,255,0.08)", borderRadius: "6px", padding: "18px 20px" }}>
              <div className="flex flex-wrap gap-4">
                <div style={{ flex: "1 1 180px" }}>
                  <label style={{ display: "block", fontSize: "11px", fontWeight: 700, color: "#c4b5fd", textTransform: "uppercase", letterSpacing: "0.07em", marginBottom: "6px" }}>Resume Updated</label>
                  <Dropdown value={resumeUpdated} options={RESUME_UPDATED_OPTIONS} onChange={(e) => setResumeUpdated(e.value)} className="w-full" style={{ fontSize: "13px" }} />
                </div>
                <div style={{ flex: "1 1 180px" }}>
                  <label style={{ display: "block", fontSize: "11px", fontWeight: 700, color: "#c4b5fd", textTransform: "uppercase", letterSpacing: "0.07em", marginBottom: "6px" }}>Education Level</label>
                  <Dropdown value={education} options={educationOptions} onChange={(e) => setEducation(e.value)} className="w-full" style={{ fontSize: "13px" }} />
                </div>
                <div style={{ flex: "1 1 180px" }}>
                  <label style={{ display: "block", fontSize: "11px", fontWeight: 700, color: "#c4b5fd", textTransform: "uppercase", letterSpacing: "0.07em", marginBottom: "6px" }}>Job Type</label>
                  <Dropdown value={jobType} options={jobTypeOptions} onChange={(e) => setJobType(e.value)} className="w-full" style={{ fontSize: "13px" }} />
                </div>
                <div style={{ flex: "1 1 180px" }}>
                  <label style={{ display: "block", fontSize: "11px", fontWeight: 700, color: "#c4b5fd", textTransform: "uppercase", letterSpacing: "0.07em", marginBottom: "6px" }}>Willing to Travel</label>
                  <Dropdown value={travel} options={travelOptions} onChange={(e) => setTravel(e.value)} className="w-full" style={{ fontSize: "13px" }} />
                </div>
              </div>

              <Divider style={{ borderColor: "rgba(255,255,255,0.15)", margin: "14px 0" }} />

              <div className="flex align-items-center gap-2">
                <Checkbox inputId="relocation" checked={relocation} onChange={(e) => setRelocation(!!e.checked)} />
                <label htmlFor="relocation" style={{ fontSize: "13px", color: "#e9d5ff", cursor: "pointer" }}>
                  Include candidates willing to relocate
                </label>
              </div>
            </div>
          )} */}
        </div>
      )}
      {showResults === false && (
        <div style={{ padding: "24px 32px" }}>
          <MonsterSavedSearchesPanel toast={toast} onRestore={handleRestoreSearch} />
        </div>
      )}
      {/* 
      <AdvancedOptions
        visible={showAdvanced}
        onHide={() => setShowAdvanced(false)}
        onSearch={() => {
          setShowAdvanced(false);
          loadTableData(buildLoadParams(searchState, {}, { pageNum: 1 }));
          setShowResults(true);
        }}
      /> */}

      {showResults && (
        <CandidateResults
          results={apiResults}
          totalResults={totalResults}
          isLoading={isLoading}
          searchQuery={searchQueryString}
          useageData={useageData}
          onNewSearch={() => {
            setShowResults(false);
            setApiResults([]);
            setFirst(1);
          }}
          onCandidateClick={getViewAccess}
          onUpdateFilters={handleUpdateFilters}
          first={first}
          setFirst={setFirst}
          onPage={onPage}
          totalReturned={totalReturned}
        />
      )}

      <Dialog header="Save this search" visible={saveDialogVisible} onHide={() => setSaveDialogVisible(false)} style={{ width: "420px" }} breakpoints={{ "640px": "90vw" }}>
        <label style={{ display: "block", fontSize: "12px", fontWeight: 700, color: "#6b21a8", textTransform: "uppercase", letterSpacing: "0.05em", marginBottom: "8px" }}>Search name</label>
        <InputText value={saveName} onChange={(e) => setSaveName(e.target.value)} placeholder={searchQueryString || "Name this search"} className="w-full mb-4" autoFocus />
        <div style={{ display: "flex", justifyContent: "flex-end", gap: "8px" }}>
          <Button label="Cancel" onClick={() => setSaveDialogVisible(false)} style={{ color: "#6b7280" }} />
          <Button label="Save Search" onClick={handleSaveSearch} style={{ backgroundColor: "#9333ea", border: "none", fontWeight: 700 }} />
        </div>
      </Dialog>

      <Toast ref={toast} />

      <Dialog
        header={selectedCandidate?.name ?? "Candidate Profile"}
        visible={profileVisible}
        style={{ width: "90vw", maxWidth: "1400px" }}
        modal
        maximizable
        onHide={() => {
          setProfileVisible(false);
          setSelectedCandidate(null);
          setProfileLoading(false);
        }}
      >
        {selectedCandidate ? (
          <>
            {profileLoading && <ProfileSkeleton />}
            <div style={{ display: profileLoading ? "none" : "block" }}>
              <MonsterCandidateProfile visible={profileVisible} singleData={{ resumeID: selectedCandidate.textResumeID }} usageData={useageData} toast={toast} onLoaded={() => setProfileLoading(false)} searchQuery={searchQueryString} />
            </div>
          </>
        ) : (
          profileLoading && <ProfileSkeleton />
        )}
      </Dialog>
    </div>
  );
};

export default SearchMonster;
